import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  CloseIcon,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@/ui';
import Footer from '@/components/on-boarding/Footer';
import SpecialScreen from '@/engine/renderer/SpecialScreen';
import Screen from '@/engine/renderer/Screen';
import { useAlert } from '@/app/providers/AlertProvider';
import { resolveCustomerId } from '@/shared/lib/customerIdResolver';
import { BudgetService, DebtsService } from '@/shared/api/services';
import type { ExpensesNoteUpdateRequest, ExpenseUpdateRequest, IncomeTriggerRecord, IncomeUpdateRequest } from '@/shared/api/types';
import type { BroadbandSupplierRecord, EnergyCompanyRecord, LandlineSupplierRecord, MobileSupplierRecord, WaterCompanyRecord } from '@/shared/api/types';
import type { ApiResponse } from '@/shared/api/types';
import { httpGet } from '@/shared/api/http';
import { customerEndpoints } from '@/shared/api/endpoints';
import { TriggersProvider } from '@/shared/warnings/TriggersContext';
import { normalizeBudgetFrequency, toMonthlyBudgetAmount } from '@/shared/lib/budgetFrequency';
import {
  mapAssetDataToSchema,
  mapExpenseDataToSchema,
  mapIncomeDataToSchema,
  mapSchemaDataToAsset,
  mapSchemaDataToAssetNotes,
  mapSchemaDataToExpense,
  mapSchemaDataToExpenseNotes,
  mapSchemaDataToIncome,
  mapSchemaDataToIncomeNotes,
} from '@/shared/lib/budgetFieldMapping';
import { mapArrearsDebtsToExpenseSchema } from '@/shared/lib/budgetFieldMapping';
import { BUDGET_FIELD_MAPPINGS } from '@/shared/lib/budgetBreakDownFields';
import salarySchema from '@/engine/schema/budget/income/1-salary.json';
import benefitsSchema from '@/engine/schema/budget/income/2-benefits.json';
import pensionSchema from '@/engine/schema/budget/income/3-pension.json';
import otherSourcesSchema from '@/engine/schema/budget/income/4-other-sources.json';
import propertiesSchema from '@/engine/schema/budget/asset/1-properties.json';
import otherAssetsSchema from '@/engine/schema/budget/asset/2-other-assets.json';
// Fixed expenses schemas
import householdSchema from '@/engine/schema/budget/fixedExpense/1-household.json';
import utilitiesSchema from '@/engine/schema/budget/fixedExpense/2-utilities.json';
import careSchema from '@/engine/schema/budget/fixedExpense/3-care.json';
import transportSchema from '@/engine/schema/budget/fixedExpense/4-transport.json';
import schoolSchema from '@/engine/schema/budget/fixedExpense/5-school.json';
import pensionInsuranceSchema from '@/engine/schema/budget/fixedExpense/6-pension-insurance.json';
import professionalsSchema from '@/engine/schema/budget/fixedExpense/7-professionals.json';
import otherSourcesFixedSchema from '@/engine/schema/budget/fixedExpense/8-other-sources.json';
// Flexible expense schemas
import leisureSchema from '@/engine/schema/budget/flexibleExpense/1-leisure.json';
import housekeepingSchema from '@/engine/schema/budget/flexibleExpense/2-housekeeping.json';
import personalCostsSchema from '@/engine/schema/budget/flexibleExpense/3-personal-costs.json';
import savingsSchemaFlex from '@/engine/schema/budget/flexibleExpense/4-savings.json';
// Debts schemas
import debtsAdministrationSchema from '@/engine/schema/budget/debts/1-debts-administration.json';
import type { OpenActionSpec } from '@/shared/warnings/types';
import { applyNISchemaTransformations } from '@/shared/lib/niSchemaTransformer';
import {
  detectSingleFieldCleanup,
  deleteCleanupRecords,
  buildCleanupConfirmMessage,
} from '@/shared/budget/uuEvidenceCleanup';
import { openConfirmDialog } from '@/components/ConfirmDialog';

export type ScreenSchema = Parameters<typeof Screen>[0]['schema'];

interface EditFieldDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  /**
   * Optional warning-action handler so parent screens (e.g. Budget Breakdown)
   * can handle profile warning tokens (employment status, accommodation, etc.)
   * exactly like UpdateBudgetPage does.
   *
   * Return `true` to mark the action as handled; otherwise EditFieldDialog will
   * fall back to its local "switch current dialog to another budget field" logic.
   */
  onWarningAction?: (
    action: OpenActionSpec,
    metadata?: { varId?: string }
  ) => boolean | void;
  groupKey?:
  | 'salary'
  | 'benefits'
  | 'pension'
  | 'otherSources'
  | 'properties'
  | 'otherAssets'
  | 'household'
  | 'utilities'
  | 'care'
  | 'transport'
  | 'school'
  | 'pensionInsurance'
  | 'professionals'
  | 'otherSourcesFixed'
  | 'leisure'
  | 'housekeeping'
  | 'personalCosts'
  | 'savings'
  | 'debtsAdministration';
  fieldKey?: string;
  title?: string;
  /** Whether the user is from Northern Ireland (for label customizations) */
  isNorthernIreland?: boolean;
  /** Expense data for NI water field visibility (hide if no existing data) */
  niExpenseData?: {
    waterBill?: number | null | undefined;
    waterWaste?: number | null | undefined;
  } | undefined;
  /** Optional joint account flag from parent context */
  withPartner?: boolean;
  /** Optional profile data from parent context */
  profileData?: any;
}

// Store values outside component to survive re-renders
let globalEditedValues: Record<string, unknown> = {};
// Store the form instance reference globally
let globalFormValues: Record<string, unknown> = {};

const EditFieldDialog: React.FC<EditFieldDialogProps> = ({
  open,
  onClose,
  onSaved,
  onWarningAction,
  groupKey: initialGroupKey = 'salary',
  fieldKey: initialFieldKey = 'takeHomeSalary',
  title: initialTitle,
  isNorthernIreland = false,
  niExpenseData,
  withPartner: withPartnerProp,
  profileData: profileDataProp,
}) => {
  // Use globalEditedValues to suppress linter warning (it's used to persist state across re-renders)
  useEffect(() => {
    // Just to use the variable
    console.debug('EditFieldDialog mounted', { hasGlobalValues: Object.keys(globalEditedValues).length > 0 });
  }, []);
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState<Record<string, unknown>>({});
  const [formRenderKey, setFormRenderKey] = useState(0);
  const [isDataReady, setIsDataReady] = useState(false);
  const liveValuesRef = useRef<Record<string, unknown>>({});
  const [incomeTriggers, setIncomeTriggers] = useState<IncomeTriggerRecord[]>([]);
  const [incomeTriggersLoaded, setIncomeTriggersLoaded] = useState(false);
  const [jointAccountFlag, setJointAccountFlag] = useState<string>('');
  const [liveTriggerValues, setLiveTriggerValues] = useState<{
    amount?: number;
    frequency?: string;
  }>({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Allow "open dialog" warning tokens to switch the current edit dialog to another field.
  // This avoids trying to open a second dialog on top of an existing one.
  const [groupKey, setGroupKey] = useState<NonNullable<EditFieldDialogProps['groupKey']>>(initialGroupKey);
  const [fieldKey, setFieldKey] = useState<string>(initialFieldKey);
  const [title, setTitle] = useState<string | undefined>(initialTitle);
  const pendingFocusPathRef = useRef<string | null>(null);
  const requestedSignatureRef = useRef<string | null>(null);

  // Sync internal active target when parent opens dialog for a different field.
  // Use layout effect so the target is updated before we kick off the initial load.
  useLayoutEffect(() => {
    if (!open) return;
    setGroupKey(initialGroupKey);
    setFieldKey(initialFieldKey);
    setTitle(initialTitle);
    pendingFocusPathRef.current = null;
    requestedSignatureRef.current = `${initialGroupKey}:${initialFieldKey}`;
  }, [open, initialGroupKey, initialFieldKey, initialTitle]);

  // Additional ref to avoid stale closures
  const latestFormValuesRef = useRef<Record<string, unknown>>({});
  // Track when user last edited to prevent resetting after edits
  const lastUserEditTime = useRef<number>(0);
  // Store the last submitted values to compare
  const lastSubmittedValues = useRef<Record<string, unknown>>({});
  // Track if we're currently submitting to prevent concurrent updates
  const isSubmittingRef = useRef(false);
  // Track if user cleared the amount field (to distinguish from "never touched")
  const fieldClearedRef = useRef<boolean>(false);
  // Track if user cleared the notes field
  const notesClearedRef = useRef<boolean>(false);
  // Preserve notes seen in the DOM even when inputs remount (SpecialScreen can re-render).
  // With exactOptionalPropertyTypes, avoid explicitly setting `value: undefined`; omit it instead.
  const lastDomNotesValueRef = useRef<{ signature: string | null; value?: string }>({
    signature: null,
  });

  // Energy companies used for Utilities (gas/electricity) supplier + arrears lists
  const [energyCompanies, setEnergyCompanies] = useState<EnergyCompanyRecord[]>([]);
  const [energyCompaniesLoaded, setEnergyCompaniesLoaded] = useState<boolean>(false);
  const fieldSignature = useMemo(() => `${groupKey}:${fieldKey}`, [groupKey, fieldKey]);

  const mapWarningActionToGroupKey = useCallback(
    (outer: unknown, inner: unknown): NonNullable<EditFieldDialogProps['groupKey']> | null => {
      if (typeof outer !== 'string' || typeof inner !== 'string') return null;
      const o = outer.trim().toLowerCase();
      const i = inner.trim().toLowerCase();

      if (o === 'income') {
        if (i === 'salary') return 'salary';
        if (i === 'benefits') return 'benefits';
        if (i === 'pension') return 'pension';
        if (i === 'other-sources' || i === 'othersources') return 'otherSources';
        if (i === 'savings') return 'savings';
      }
      if (o === 'assets') {
        if (i === 'properties') return 'properties';
        if (i === 'other-assets' || i === 'otherassets') return 'otherAssets';
      }
      if (o === 'fixed-expenses') {
        if (i === 'household') return 'household';
        if (i === 'utilities') return 'utilities';
        if (i === 'care') return 'care';
        if (i === 'transport') return 'transport';
        if (i === 'school') return 'school';
        if (i === 'pension-insurance' || i === 'pensioninsurance')
          return 'pensionInsurance';
        if (i === 'professionals') return 'professionals';
        if (i === 'other-sources' || i === 'othersources') return 'otherSourcesFixed';
      }
      if (o === 'flexible-expenses') {
        if (i === 'leisure') return 'leisure';
        if (i === 'housekeeping') return 'housekeeping';
        if (i === 'personal-costs' || i === 'personalcosts') return 'personalCosts';
        if (i === 'savings') return 'savings';
      }
      if (o === 'debts') {
        if (i === 'debts-administration' || i === 'debtsadministration')
          return 'debtsAdministration';
      }

      return null;
    },
    []
  );

  const deriveFieldKeyFromFocus = useCallback((focus: string): string | null => {
    const parts = focus.split('.').filter(Boolean);
    if (parts.length === 0) return null;
    if (parts.length === 1) return parts[0] ?? null;
    // Prefer the second-to-last segment: "houseRent.amount" -> "houseRent",
    // "hobbiesLeisureSports.gamblingFromHobbiesLeisure.amount" -> "gamblingFromHobbiesLeisure"
    return parts[parts.length - 2] ?? null;
  }, []);

  const focusFieldByPath = useCallback((fieldPath: string) => {
    if (typeof window === 'undefined') return;
    try {
      window.dispatchEvent(
        new CustomEvent('iehub:focus-field', { detail: { fieldPath } })
      );
    } catch {
      // no-op
    }
  }, []);

  // After (re)loading a different field, apply any pending focus request.
  useEffect(() => {
    if (!open) return;
    if (!isDataReady) return;
    const path = pendingFocusPathRef.current;
    if (!path) return;
    pendingFocusPathRef.current = null;
    // Delay slightly so the field mounts before we dispatch focus
    window.setTimeout(() => {
      focusFieldByPath(path);
    }, 120);
  }, [open, isDataReady, formRenderKey, focusFieldByPath]);

  // Leisure supplier lists (used by landline/broadband/mobile selects)
  const [broadbandSuppliers, setBroadbandSuppliers] = useState<BroadbandSupplierRecord[]>([]);
  const [broadbandSuppliersLoaded, setBroadbandSuppliersLoaded] = useState<boolean>(false);
  const [landlineSuppliers, setLandlineSuppliers] = useState<LandlineSupplierRecord[]>([]);
  const [landlineSuppliersLoaded, setLandlineSuppliersLoaded] = useState<boolean>(false);
  const [mobileSuppliers, setMobileSuppliers] = useState<MobileSupplierRecord[]>([]);
  const [mobileSuppliersLoaded, setMobileSuppliersLoaded] = useState<boolean>(false);

  // Water companies for water/waterWaste arrears dialogs
  const [waterCompanies, setWaterCompanies] = useState<WaterCompanyRecord[]>([]);
  const [waterCompaniesLoaded, setWaterCompaniesLoaded] = useState<boolean>(false);

  // Load energy companies with NI-specific filtering
  useEffect(() => {
    if (!open) return;
    if (groupKey !== 'utilities') return;
    if (energyCompaniesLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await BudgetService.fetchEnergyCompanies({
          skipErrorCapture: false,
          isNorthernIreland: isNorthernIreland ?? false,
        });
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          setEnergyCompanies(resp.data);
        } else {
          setEnergyCompanies([]);
        }
        setEnergyCompaniesLoaded(true);
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load energy companies:', e);
          setEnergyCompanies([]);
          setEnergyCompaniesLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, groupKey, energyCompaniesLoaded, isNorthernIreland]);

  // Load water companies when the utilities dialog opens
  useEffect(() => {
    if (!open) return;
    if (groupKey !== 'utilities') return;
    if (waterCompaniesLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await BudgetService.fetchWaterCompanies({ skipErrorCapture: false });
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          setWaterCompanies(resp.data);
        } else {
          setWaterCompanies([]);
        }
        setWaterCompaniesLoaded(true);
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load water companies:', e);
          setWaterCompanies([]);
          setWaterCompaniesLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, groupKey, waterCompaniesLoaded]);

  // Load income/expense triggers once so warning engine can show limit prompts while typing
  useEffect(() => {
    if (!open) return;
    if (incomeTriggersLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await BudgetService.getIncomeTriggers({ skipErrorCapture: true });
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          setIncomeTriggers(resp.data);
        } else {
          setIncomeTriggers([]);
        }
        setIncomeTriggersLoaded(true);
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load triggers:', e);
          setIncomeTriggers([]);
          setIncomeTriggersLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, incomeTriggersLoaded]);

  // Load leisure suppliers when editing leisure fields
  useEffect(() => {
    if (!open) return;
    if (groupKey !== 'leisure') return;

    let cancelled = false;

    const loadBroadband = async () => {
      if (broadbandSuppliersLoaded) return;
      try {
        const resp = await BudgetService.fetchBroadbandSuppliers({ skipErrorCapture: false });
        if (cancelled) return;
        setBroadbandSuppliers(Array.isArray(resp.data) ? resp.data : []);
        setBroadbandSuppliersLoaded(true);
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load broadband suppliers:', e);
          setBroadbandSuppliers([]);
          setBroadbandSuppliersLoaded(true);
        }
      }
    };

    const loadLandline = async () => {
      if (landlineSuppliersLoaded) return;
      try {
        const resp = await BudgetService.fetchLandlineSuppliers({ skipErrorCapture: false });
        if (cancelled) return;
        setLandlineSuppliers(Array.isArray(resp.data) ? resp.data : []);
        setLandlineSuppliersLoaded(true);
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load landline suppliers:', e);
          setLandlineSuppliers([]);
          setLandlineSuppliersLoaded(true);
        }
      }
    };

    const loadMobile = async () => {
      if (mobileSuppliersLoaded) return;
      try {
        const resp = await BudgetService.fetchMobileSuppliers({ skipErrorCapture: false });
        if (cancelled) return;
        setMobileSuppliers(Array.isArray(resp.data) ? resp.data : []);
        setMobileSuppliersLoaded(true);
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load mobile suppliers:', e);
          setMobileSuppliers([]);
          setMobileSuppliersLoaded(true);
        }
      }
    };

    void loadBroadband();
    void loadLandline();
    void loadMobile();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    groupKey,
    broadbandSuppliersLoaded,
    landlineSuppliersLoaded,
    mobileSuppliersLoaded,
  ]);

  // Build a schema that contains only the target field for inline edit
  const dialogSchema: ScreenSchema | null = useMemo(() => {
    try {
      const base =
        groupKey === 'salary'
          ? salarySchema
          : groupKey === 'benefits'
            ? benefitsSchema
            : groupKey === 'pension'
              ? pensionSchema
              : groupKey === 'otherSources'
                ? otherSourcesSchema
                : groupKey === 'properties'
                  ? propertiesSchema
                  : groupKey === 'otherAssets'
                    ? otherAssetsSchema
                    : groupKey === 'household'
                      ? householdSchema
                      : groupKey === 'utilities'
                        ? utilitiesSchema
                        : groupKey === 'care'
                          ? careSchema
                          : groupKey === 'transport'
                            ? transportSchema
                            : groupKey === 'school'
                              ? schoolSchema
                              : groupKey === 'pensionInsurance'
                                ? pensionInsuranceSchema
                                : groupKey === 'professionals'
                                  ? professionalsSchema
                                  : groupKey === 'otherSourcesFixed'
                                    ? otherSourcesFixedSchema
                                    : groupKey === 'leisure'
                                      ? leisureSchema
                                      : groupKey === 'debtsAdministration'
                                        ? debtsAdministrationSchema
                                        : groupKey === 'housekeeping'
                                          ? housekeepingSchema
                                          : groupKey === 'personalCosts'
                                            ? personalCostsSchema
                                            : groupKey === 'savings'
                                              ? (savingsSchemaFlex as unknown as ScreenSchema)
                                              : salarySchema;
      const cloned = JSON.parse(JSON.stringify(base));
      const section = cloned.sections?.[0];
      if (section && Array.isArray(section.fields)) {
        // First try to find field at top level
        let found = section.fields.find((f: any) => f.fieldName === fieldKey);
        let savingsWarning: any = null;

        // If not found, search in nested children (for gamblingFromHobbiesLeisure)
        if (!found) {
          for (const field of section.fields) {
            if (field.children && Array.isArray(field.children)) {
              const nestedFound = field.children.find((child: any) => child.fieldName === fieldKey);
              if (nestedFound) {
                found = nestedFound;
                break;
              }
            }
          }
        }

        // Fallback to first field if still not found
        if (!found) {
          found = section.fields[0];
        }

        // Savings edit dialog: include the live warning info-box and keep it reactive
        if (groupKey === 'savings' && fieldKey === 'savingsAmount') {
          // Keep the savingsAmount field always visible inside dialog
          if (found && found.visibleWhen) delete found.visibleWhen;
          // Find the warning box from original schema
          savingsWarning = section.fields.find((f: any) => f.fieldName === 'savingsAmountWarningBox') || null;
          if (savingsWarning) {
            // Make the warning depend only on amount > 25 so it reacts while typing
            savingsWarning.visibleWhen = {
              op: 'gt',
              args: [{ var: 'savingsAmount.amount' }, 25],
            };
          }
          section.fields = [found, ...(savingsWarning ? [savingsWarning] : [])];
        } else {
          if (found && found.visibleWhen) delete found.visibleWhen;
          section.fields = [found];
        }
      }
      return cloned as ScreenSchema;
    } catch {
      return null;
    }
  }, [groupKey, fieldKey]);

  // Enhance Utilities schema with energy/water company lists and arrears company dropdown mirroring updateBudgetPage
  const enhancedSchema: ScreenSchema | null = useMemo(() => {
    if (!dialogSchema) return dialogSchema;

    if (groupKey === 'utilities') {
      // Water/waterWaste arrears: use water companies list
      if (fieldKey === 'water' || fieldKey === 'waterWaste') {
        if (!waterCompaniesLoaded) return dialogSchema;
        try {
          const cloned = JSON.parse(JSON.stringify(dialogSchema)) as any;
          const section = cloned?.sections?.[0];
          if (!section || !Array.isArray(section.fields) || !section.fields[0]) {
            return cloned as ScreenSchema;
          }
          const targetField = section.fields[0];

          const buildWaterOptionsForDialog = (): Array<{ value: string; label: string }> => {
            const manualTopOptionDialog = { value: '__manual__', label: "I can't find my company" };
            const seen = new Set<string>();
            return [
              manualTopOptionDialog,
              ...(waterCompanies || [])
                .map((c) => ({ value: c.companyName, label: c.companyName }))
                .filter((opt) => {
                  const key = (opt.label || '').trim().toLowerCase();
                  if (!key || seen.has(key)) return false;
                  seen.add(key);
                  return true;
                })
                .sort((a, b) => a.label.localeCompare(b.label)),
            ];
          };

          targetField.ui = {
            ...(targetField.ui ?? {}),
            arrearsConfig: {
              ...(targetField.ui?.arrearsConfig ?? {}),
              companies: buildWaterOptionsForDialog(),
            },
          };

          return cloned as ScreenSchema;
        } catch (e) {
          console.error('Failed to enhance utilities schema for water companies:', e);
          return dialogSchema;
        }
      }

      // Gas/electricity: use energy companies list
      if (!energyCompaniesLoaded) return dialogSchema;
      try {
        const cloned = JSON.parse(JSON.stringify(dialogSchema)) as any;
        const section = cloned?.sections?.[0];
        if (!section || !Array.isArray(section.fields) || !section.fields[0]) {
          return cloned as ScreenSchema;
        }
        const targetField = section.fields[0];
        if (!Array.isArray(targetField.children)) {
          return cloned as ScreenSchema;
        }

        const buildCompanyOptionsForMain = (): Array<{ value: string; label: string }> => {
          const manualTopOptionMain = { value: 'Other', label: 'I can\'t find my company' };
          const seen = new Set<string>();
          const mapped = (energyCompanies || [])
            .map((c) => ({ value: c.companyName, label: c.companyName }))
            // remove any API-provided "Other"
            .filter((opt) => {
              const val = String(opt.value || '').trim().toLowerCase();
              const lbl = String(opt.label || '').trim().toLowerCase();
              return val !== 'other' && lbl !== 'other';
            })
            .filter((opt) => {
              const key = (opt.label || '').trim().toLowerCase();
              if (!key || seen.has(key)) return false;
              seen.add(key);
              return true;
            })
            .sort((a, b) => a.label.localeCompare(b.label));
          return [manualTopOptionMain, ...mapped];
        };

        const buildCompanyOptionsForDialog = (): Array<{ value: string; label: string }> => {
          const manualTopOptionDialog = { value: '__manual__', label: 'I can\'t find my company' };
          const seen = new Set<string>();
          return [
            manualTopOptionDialog,
            ...(energyCompanies || [])
              .map((c) => ({ value: c.companyName, label: c.companyName }))
              .filter((opt) => {
                const key = (opt.label || '').trim().toLowerCase();
                if (!key || seen.has(key)) return false;
                seen.add(key);
                return true;
              })
              .sort((a, b) => a.label.localeCompare(b.label)),
          ];
        };

        const getString = (v: unknown): string => (typeof v === 'string' ? v : '');
        const fieldValues = (initialData as any)?.[fieldKey] || {};
        const currentAmount = getString(fieldValues?.amount);
        const currentFrequency = getString(fieldValues?.frequency);
        const currentSupplier =
          fieldKey === 'gas'
            ? getString(fieldValues?.gasSupplier)
            : fieldKey === 'electricity'
              ? getString(fieldValues?.electricitySupplier)
              : '';
        const currentSupplierOther =
          fieldKey === 'gas'
            ? getString(fieldValues?.gasSupplierOther)
            : fieldKey === 'electricity'
              ? getString(fieldValues?.electricitySupplierOther)
              : '';

        targetField.children = targetField.children.map((child: any) => {
          // Supplier select on the main field
          if (
            (fieldKey === 'gas' && child.fieldName === 'gasSupplier') ||
            (fieldKey === 'electricity' && child.fieldName === 'electricitySupplier')
          ) {
            const existingEvents = child.events ?? {};
            const existingOnChange = Array.isArray(existingEvents.onChange) ? existingEvents.onChange : [];
            const resetManualSupplierOnChange =
              fieldKey === 'gas'
                ? {
                  when: {
                    op: 'and',
                    args: [
                      { op: 'ne', args: [{ var: '$value' }, 'Other'] },
                      { op: 'ne', args: [{ var: '$value' }, 'I can\'t find my company'] },
                    ],
                  },
                  actions: [{ type: 'setFieldValue', name: 'gas.gasSupplierOther', value: '' }],
                }
                : {
                  when: {
                    op: 'and',
                    args: [
                      { op: 'ne', args: [{ var: '$value' }, 'Other'] },
                      { op: 'ne', args: [{ var: '$value' }, 'I can\'t find my company'] },
                    ],
                  },
                  actions: [{ type: 'setFieldValue', name: 'electricity.electricitySupplierOther', value: '' }],
                };

            const hasManualReset = existingOnChange.some(
              (event: { actions?: Array<{ name?: unknown }> } | null | undefined) =>
                Array.isArray(event?.actions) &&
                event!.actions!.some((a: { name?: unknown }) =>
                  fieldKey === 'gas'
                    ? a?.name === 'gas.gasSupplierOther'
                    : a?.name === 'electricity.electricitySupplierOther',
                ),
            );
            const mergedEvents = {
              ...existingEvents,
              onChange: hasManualReset
                ? existingOnChange
                : [...existingOnChange, resetManualSupplierOnChange],
            };
            return {
              ...child,
              options: buildCompanyOptionsForMain(),
              events: mergedEvents,
            };
          }

          // Arrears dialog nested list
          if (child.fieldName === 'arrears' && Array.isArray(child.children)) {
            child.children = child.children.map((lf: any) => {
              if (lf.fieldName === 'companyName') {
                const companyDefault =
                  currentSupplier && currentSupplier.trim().length > 0
                    ? currentSupplier === 'Other'
                      ? '__manual__'
                      : currentSupplier
                    : undefined;
                return {
                  ...lf,
                  dataType: 'select',
                  options: buildCompanyOptionsForDialog(),
                  ...(companyDefault ? { defaultValue: companyDefault } : {}),
                  ui: {
                    ...(lf.ui ?? {}),
                    searchable: true,
                    placeholder: 'Select a Company',
                    dialogLabel: 'Select a Company',
                  },
                };
              }
              if (lf.fieldName === 'companyNameManual') {
                const manualDefault =
                  currentSupplier === 'Other' && currentSupplierOther.trim().length > 0
                    ? currentSupplierOther
                    : undefined;
                return {
                  ...lf,
                  ...(manualDefault ? { defaultValue: manualDefault } : {}),
                };
              }
              if (lf.fieldName === 'amountCurrentlyOwned') {
                return {
                  ...lf,
                  label: 'Amount currently owed',
                  defaultValue: currentAmount,
                };
              }
              if (lf.fieldName === 'frequency') {
                return {
                  ...lf,
                  defaultValue: currentFrequency,
                };
              }
              if (lf.fieldName === 'accountType') {
                const autoDefault =
                  Array.isArray(lf.options) && lf.options.length === 1 ? (lf.options[0] as any)?.value : undefined;
                return {
                  ...lf,
                  label: 'Arrears Type',
                  isRequired: true,
                  ...(autoDefault ? { defaultValue: autoDefault } : {}),
                  ui: {
                    ...(lf.ui ?? {}),
                    dialogLabel: 'Select arrears type',
                  },
                };
              }
              return lf;
            });
            return child;
          }
          return child;
        });

        return cloned as ScreenSchema;
      } catch (e) {
        console.error('Failed to enhance utilities schema for energy companies:', e);
        return dialogSchema;
      }
    }

    // Leisure enhancement: landline/broadband/mobile suppliers
    if (groupKey === 'leisure') {
      // Enhance only when lists are loaded; otherwise return the base dialog schema
      if (
        !broadbandSuppliersLoaded &&
        !landlineSuppliersLoaded &&
        !mobileSuppliersLoaded
      ) {
        return dialogSchema;
      }
      try {
        const cloned = JSON.parse(JSON.stringify(dialogSchema)) as any;
        const section = cloned?.sections?.[0];
        if (!section || !Array.isArray(section.fields) || !section.fields[0]) {
          return cloned as ScreenSchema;
        }
        const targetField = section.fields[0];
        if (!Array.isArray(targetField.children)) {
          return cloned as ScreenSchema;
        }

        const manualTopOption = { value: 'Other', label: 'Other' };
        const toOptions = (arr: Array<{ supplierName: string }>) => {
          const seen = new Set<string>();
          const mapped = (Array.isArray(arr) ? arr : [])
            .map((s) => ({
              value: s.supplierName,
              label: s.supplierName,
            }))
            .filter((opt) => {
              const val = String(opt.value || '').trim().toLowerCase();
              const lbl = String(opt.label || '').trim().toLowerCase();
              return val !== 'other' && lbl !== 'other';
            })
            .filter((opt) => {
              const key = (opt.label || '').trim().toLowerCase();
              if (!key || seen.has(key)) return false;
              seen.add(key);
              return true;
            })
            .sort((a, b) => a.label.localeCompare(b.label));
          return [manualTopOption, ...mapped];
        };

        targetField.children = targetField.children.map((child: any) => {
          if (fieldKey === 'landlineInternetTV' && child.fieldName === 'landlineSupplier' && landlineSuppliersLoaded) {
            return {
              ...child,
              options: toOptions(landlineSuppliers as any),
            };
          }
          if (fieldKey === 'landlineInternetTV' && child.fieldName === 'broadbandSupplier' && broadbandSuppliersLoaded) {
            return {
              ...child,
              options: toOptions(broadbandSuppliers as any),
            };
          }
          if (fieldKey === 'mobilePhones' && child.fieldName === 'mobileSupplier' && mobileSuppliersLoaded) {
            return {
              ...child,
              options: toOptions(mobileSuppliers as any),
            };
          }
          return child;
        });

        return cloned as ScreenSchema;
      } catch (e) {
        console.error('Failed to enhance leisure schema for suppliers:', e);
        return dialogSchema;
      }
    }

    try {
      // Default: no enhancement
      return dialogSchema;
    } catch (e) {
      console.error('Failed to build enhanced schema:', e);
      return dialogSchema;
    }
  }, [
    dialogSchema,
    groupKey,
    fieldKey,
    energyCompaniesLoaded,
    energyCompanies,
    initialData,
    broadbandSuppliersLoaded,
    landlineSuppliersLoaded,
    mobileSuppliersLoaded,
    broadbandSuppliers,
    landlineSuppliers,
    mobileSuppliers,
    waterCompaniesLoaded,
    waterCompanies,
  ]);

  // Apply Northern Ireland label transformations (council rates, water hiding, savings)
  const niEnhancedSchema: ScreenSchema | null = useMemo(() => {
    if (!enhancedSchema) return enhancedSchema;
    if (!isNorthernIreland) return enhancedSchema;
    
    // Clone and apply NI transformations
    // Pass expense data so water fields can be hidden for NI users without existing data
    const cloned = JSON.parse(JSON.stringify(enhancedSchema));
    applyNISchemaTransformations(cloned, isNorthernIreland, {
      expenseData: niExpenseData,
    });
    return cloned as ScreenSchema;
  }, [enhancedSchema, isNorthernIreland, niExpenseData]);

  const formId = useMemo(() => `edit-income-${groupKey}-${fieldKey}-form`, [groupKey, fieldKey]);

  // Build a triggers context that updates as the user types, so trigger-based warnings
  // ("Quick check" limit prompts) appear in this inline dialog exactly like updateBudgetPage.
  const triggersContext = useMemo(() => {
    const empty = {
      income: { exceeded: {}, maxByField: {} },
      expense: { exceeded: {}, maxByField: {} },
      maxByField: {},
    } as {
      income: { exceeded: Record<string, boolean>; maxByField: Record<string, number> };
      expense: { exceeded: Record<string, boolean>; maxByField: Record<string, number> };
      maxByField: Record<string, number>;
    };

    const toUpperSnake = (apiFieldName: string): string =>
      apiFieldName.replace(/([A-Z])/g, '_$1').toUpperCase().replace(/^_/, '');

    const normalizeTriggerFieldUpper = (raw: unknown): string => {
      const s =
        typeof raw === 'string'
          ? raw
          : raw === null || raw === undefined
            ? ''
            : String(raw);
      const trimmed = s.trim();
      if (!trimmed) return '';

      let out = trimmed
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .toUpperCase();

      out = out.replace(/([A-Z])(\d+)$/g, '$1_$2');
      return out;
    };

    const normalizeToMonthly = (amount: number | undefined, freqRaw: unknown): number | undefined => {
      if (typeof amount !== 'number' || Number.isNaN(amount)) return amount;
      const freq = normalizeBudgetFrequency(freqRaw) ?? 'monthly';
      return toMonthlyBudgetAmount(amount, freq);
    };

    const mapping = (BUDGET_FIELD_MAPPINGS as any)?.[groupKey]?.[fieldKey];
    const apiAmountField = mapping?.amount as string | undefined;
    if (!apiAmountField) return empty;
    if (!Array.isArray(incomeTriggers) || incomeTriggers.length === 0) return empty;

    const targetUpper = normalizeTriggerFieldUpper(toUpperSnake(apiAmountField));
    if (!targetUpper) return empty;

    const matching = (incomeTriggers as any[]).find((t) => {
      const keys = [t?.fieldName, t?.columnName]
        .filter((v: unknown) => typeof v === 'string' && String(v).trim().length > 0)
        .map((v: string) => v.trim());
      return keys.some((k) => {
        const upper = normalizeTriggerFieldUpper(k);
        return upper === targetUpper || upper.replace(/_/g, '') === targetUpper.replace(/_/g, '');
      });
    }) as any;
    if (!matching) return empty;

    const rawKeys = [matching?.fieldName, matching?.columnName]
      .filter((v: unknown) => typeof v === 'string' && String(v).trim().length > 0)
      .map((v: string) => v.trim());
    const rawUpperKeys = rawKeys
      .map((k) => normalizeTriggerFieldUpper(k))
      .filter(Boolean) as string[];
    const canonicalUpper = targetUpper;
    const triggerKeyAliases = Array.from(
      new Set(
        [
          ...rawUpperKeys,
          ...rawUpperKeys.map((k) => k.replace(/_/g, '')),
          canonicalUpper,
          canonicalUpper.replace(/_/g, ''),
        ].filter(Boolean),
      ),
    );

    const incomeType = String(matching?.incomeType ?? '').toLowerCase() as 'income' | 'asset' | 'expense' | '';
    const group: 'income' | 'expense' | 'assets' =
      incomeType === 'expense' ? 'expense' : incomeType === 'asset' ? 'assets' : 'income';

    let storedCustomerData: any = null;
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem('customerData') : null;
      storedCustomerData = raw ? JSON.parse(raw) : null;
    } catch {
      // ignore
    }

    const withPartner =
      withPartnerProp === true ||
      String(jointAccountFlag || '').toUpperCase() === 'Y' ||
      storedCustomerData?.budgetWithPartner === true ||
      (profileDataProp as any)?.budgetWithPartner === true;

    const capUpper =
      withPartner && typeof (matching as any)?.upperLimitJoint === 'number'
        ? (matching as any).upperLimitJoint
        : matching?.upperLimit;

    // Dynamic CHILD_BENEFIT cap based on dependents (best-effort from localStorage)
    let effectiveUpper: number | undefined = capUpper;
    if (triggerKeyAliases.includes('CHILD_BENEFIT')) {
      let numChildren = 0;
      if (storedCustomerData) {
        const depU16 =
          Number(storedCustomerData.dependentChildrenUnder16 ?? storedCustomerData.dependantsUnder16 ?? 0) || 0;
        const dep16P =
          Number(
            storedCustomerData.dependentChildrenAged16Plus ??
              storedCustomerData.dependentChildrenaged16plus ??
              0,
          ) || 0;
        numChildren = depU16 + dep16P;
      }
      const eldest = typeof matching?.eldestChild === 'number' ? matching.eldestChild : undefined;
      const additional = typeof matching?.additionalChild === 'number' ? matching.additionalChild : undefined;
      if (eldest !== undefined && additional !== undefined && numChildren > 0) {
        effectiveUpper = eldest + Math.max(0, numChildren - 1) * additional;
      }
    }

    const readNumberMaybe = (v: unknown): number | undefined => {
      if (typeof v === 'number' && Number.isFinite(v)) return v;
      if (typeof v === 'string' && v.trim() !== '') {
        const n = Number(v.replace(/,/g, ''));
        if (Number.isFinite(n)) return n;
      }
      return undefined;
    };

    const amountRaw = liveTriggerValues?.amount;
    const numericVal = readNumberMaybe(amountRaw);
    const monthlyVal = normalizeToMonthly(numericVal, liveTriggerValues?.frequency);

    const belowLower =
      typeof matching?.lowerLimit === 'number' &&
      typeof monthlyVal === 'number' &&
      Math.round(monthlyVal * 100) / 100 < Math.round(matching.lowerLimit * 100) / 100;
    const aboveUpper =
      typeof effectiveUpper === 'number' &&
      typeof monthlyVal === 'number' &&
      Math.round(monthlyVal * 100) / 100 > Math.round(effectiveUpper * 100) / 100;
    const isExceeded = Boolean(belowLower || aboveUpper);

    const incomeExceeded: Record<string, boolean> = {};
    const incomeMaxByField: Record<string, number> = {};
    const expenseExceeded: Record<string, boolean> = {};
    const expenseMaxByField: Record<string, number> = {};
    const rootMaxByField: Record<string, number> = {};

    if (typeof effectiveUpper === 'number') {
      triggerKeyAliases.forEach((k) => {
        rootMaxByField[k] = effectiveUpper as number;
        if (group === 'expense') expenseMaxByField[k] = effectiveUpper as number;
        else incomeMaxByField[k] = effectiveUpper as number;
      });
    }

    if (isExceeded) {
      triggerKeyAliases.forEach((k) => {
        if (group === 'expense') expenseExceeded[k] = true;
        else incomeExceeded[k] = true;
      });
    }

    return {
      income: { exceeded: incomeExceeded, maxByField: incomeMaxByField },
      expense: { exceeded: expenseExceeded, maxByField: expenseMaxByField },
      maxByField: rootMaxByField,
    };
  }, [groupKey, fieldKey, incomeTriggers, liveTriggerValues, jointAccountFlag]);

  const getUserId = useCallback((): number | null => {
    try {
      const storedUserRaw = typeof window !== 'undefined' ? localStorage.getItem('userData') : null;
      const storedUser = storedUserRaw ? JSON.parse(storedUserRaw) : null;
      const userId = storedUser?.userId;
      return typeof userId === 'number' ? userId : null;
    } catch {
      return null;
    }
  }, []);

  const loadInitial = useCallback(async () => {
    // Don't load if we're submitting
    if (isSubmittingRef.current) {
      return;
    }

    try {
      setLoading(true);
      setIsDataReady(false);
      const customerId = await resolveCustomerId();
      if (!customerId) {
        showAlert({ severity: 'error', message: 'Customer ID not found', autoHide: true, duration: 2500 });
        return;
      }
      // Fetch calculated totals for dynamic help text (balanceAmount and totalOutgoings)
      let dynamicMeta: Record<string, unknown> = {};
      try {
        const totalsResp = await httpGet<ApiResponse<any>>(
          customerEndpoints.getCalculatedTotals(customerId),
        );
        const raw = (totalsResp.data?.data ?? totalsResp.data) as any[] | any | null;
        const totals = Array.isArray(raw) ? raw[0] : raw;
        const toNumber = (v: unknown): number => {
          const n =
            typeof v === 'string'
              ? parseFloat(v)
              : typeof v === 'number'
                ? v
                : Number(v);
          return Number.isFinite(n) ? n : 0;
        };
        const format = (n: number): string =>
          n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const fixed = toNumber(totals?.totalOutgoingsFixed);
        const flexible = toNumber(totals?.totalOutgoingsFlexible);
        const bal = toNumber(totals?.balanceAmount);
        dynamicMeta = {
          balanceAmount: format(bal),
          totalOutgoings: format(fixed + flexible),
        };
      } catch {
        // Fallback to zeros to avoid empty placeholders in help text
        const format = (n: number): string =>
          n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        dynamicMeta = {
          balanceAmount: format(0),
          totalOutgoings: format(0),
        };
      }
      // Determine data type by groupKey
      const isAssetGroup = groupKey === 'properties' || groupKey === 'otherAssets';
      const isIncomeGroup =
        groupKey === 'salary' ||
        groupKey === 'benefits' ||
        groupKey === 'pension' ||
        groupKey === 'otherSources' ||
        groupKey === 'savings';
      const isExpenseGroup = !isAssetGroup && !isIncomeGroup;

      let dataRecord: any = undefined;
      let notesRecord: any = {};

      if (isAssetGroup) {
        const [assetResponse, assetNotesResponse, incomeFallbackResponse, incomeNotesFallbackResponse] =
          await Promise.all([
            BudgetService.getAssetData(customerId, { skipCache: true }).catch((err) => {
              console.error('Failed to fetch asset data for asset group:', err);
              return { success: false, data: [] };
            }),
            BudgetService.getAssetNotes(customerId).catch((err) => {
              console.error('Failed to fetch asset notes for asset group:', err);
              return { success: false, data: [] };
            }),
            BudgetService.getIncomeData(customerId, { skipCache: true }).catch(() => ({
              success: false,
              data: [],
            })),
            BudgetService.getIncomeNotes(customerId).catch(() => ({ success: false, data: [] })),
          ]);
        const assetRecord =
          assetResponse.success && Array.isArray(assetResponse.data) ? assetResponse.data[0] : undefined;
        const assetNotes =
          assetNotesResponse.success && Array.isArray(assetNotesResponse.data) ? assetNotesResponse.data[0] : {};

        if (assetRecord) {
          dataRecord = assetRecord;
          notesRecord = assetNotes;
        } else {
          // Fall back to income dataset (legacy storage) so previously saved values still appear
          dataRecord =
            incomeFallbackResponse.success && Array.isArray(incomeFallbackResponse.data)
              ? incomeFallbackResponse.data[0]
              : undefined;
          notesRecord =
            incomeNotesFallbackResponse.success && Array.isArray(incomeNotesFallbackResponse.data)
              ? incomeNotesFallbackResponse.data[0]
              : {};
        }
      } else if (isIncomeGroup) {
        const [incomeResponse, incomeNotesResponse] = await Promise.all([
          BudgetService.getIncomeData(customerId, { skipCache: true }),
          BudgetService.getIncomeNotes(customerId),
        ]);
        dataRecord =
          incomeResponse.success && Array.isArray(incomeResponse.data) ? incomeResponse.data[0] : undefined;
        notesRecord =
          incomeNotesResponse.success && Array.isArray(incomeNotesResponse.data)
            ? incomeNotesResponse.data[0]
            : {};
      } else if (isExpenseGroup) {
        const [expenseResponse, expenseNotesResponse] = await Promise.all([
          BudgetService.getExpenseData(customerId, { skipCache: true }).catch((err) => {
            console.error('Failed to fetch expense data:', err);
            return { success: false, data: [] };
          }),
          BudgetService.getExpenseNotes(customerId).catch((err) => {
            console.error('Failed to fetch expense notes:', err);
            return { success: false, data: [] };
          }),
        ]);
        dataRecord =
          expenseResponse.success && Array.isArray(expenseResponse.data) ? expenseResponse.data[0] : undefined;
        notesRecord =
          expenseNotesResponse.success && Array.isArray(expenseNotesResponse.data)
            ? expenseNotesResponse.data[0]
            : {};
      }

      if (dataRecord) {
        // Keep a best-effort joint-account signal for trigger caps (upperLimitJoint)
        // so limit prompts behave like updateBudgetPage while editing.
        try {
          const ja = (dataRecord as any)?.jointAccount;
          if (typeof ja === 'string') {
            setJointAccountFlag(ja);
          } else if (typeof ja === 'boolean') {
            setJointAccountFlag(ja ? 'Y' : 'N');
          }
        } catch {
          // ignore
        }

        const mapped = isAssetGroup
          ? mapAssetDataToSchema(dataRecord as any, (notesRecord as any) || {}, groupKey as any)
          : isExpenseGroup
            ? mapExpenseDataToSchema(dataRecord as any, (notesRecord as any) || {}, groupKey as any)
            : mapIncomeDataToSchema(dataRecord as any, (notesRecord as any) || {}, groupKey as any);

        // Merge arrears from Debts API for supported expense categories (household, utilities)
        let mergedForArrears: Record<string, any> = mapped ? { ...(mapped as any) } : {};
        if (isExpenseGroup && (groupKey === 'household' || groupKey === 'utilities')) {
          try {
            const debtsResp = await DebtsService.getDebts(customerId).catch(() => ({ success: false, data: [] }));
            if (debtsResp?.success && Array.isArray(debtsResp.data) && debtsResp.data.length > 0) {
              const arrearsData = mapArrearsDebtsToExpenseSchema(debtsResp.data as any, groupKey as any);
              Object.keys(arrearsData).forEach((fieldName) => {
                const entry = (arrearsData as any)[fieldName];
                if (entry && entry.arrears) {
                  if (!mergedForArrears[fieldName] || typeof mergedForArrears[fieldName] !== 'object') {
                    mergedForArrears[fieldName] = {};
                  }
                  mergedForArrears[fieldName].arrears = entry.arrears;
                  mergedForArrears[fieldName].inArrears =
                    Array.isArray(entry.arrears) && entry.arrears.length > 0;
                }
              });
            }
          } catch (e) {
            console.warn('Arrears merge skipped due to fetch error:', e);
          }
        }

        // Handle special nested structure for gamblingFromHobbiesLeisure
        let selected = (mergedForArrears as any)?.[fieldKey];
        if (groupKey === 'leisure' && fieldKey === 'gamblingFromHobbiesLeisure') {
          // Check if data exists in nested structure under hobbiesLeisureSports
          const hobbiesData = (mergedForArrears as any)?.['hobbiesLeisureSports'];
          if (hobbiesData && hobbiesData['gamblingFromHobbiesLeisure']) {
            selected = hobbiesData['gamblingFromHobbiesLeisure'];
          }
        }

        // Cross-section hydration for warning engine (legacy parity):
        // The HB-vs-Rent prompt needs access to Housing Benefit values while editing Rent.
        // The dialog normally seeds only the current field, so we add the relevant income group.
        let crossSectionSeed: Record<string, any> = {};

        // "Dependent on Universal Credit" warnings need the UC amount available
        // alongside the field being edited. Since the dialog seeds only the current
        // field, inject the UC value from the already-fetched benefits data.
        const UC_DEPENDENT_FIELDS = new Set([
          'jobSeekerAllowanceContribute',
          'empsupportallowancestatutory',
          'incomeSupport',
        ]);
        if (groupKey === 'benefits' && UC_DEPENDENT_FIELDS.has(fieldKey)) {
          const ucData = (mergedForArrears as any)?.universalCredit;
          if (ucData) {
            crossSectionSeed.universalCredit = ucData;
          }
        }

        if (isExpenseGroup && fieldKey === 'houseRent') {
          try {
            const [incomeResponse, incomeNotesResponse] = await Promise.all([
              BudgetService.getIncomeData(customerId, { skipCache: true }).catch(() => ({
                success: false,
                data: [],
              })),
              BudgetService.getIncomeNotes(customerId).catch(() => ({
                success: false,
                data: [],
              })),
            ]);
            const incomeRecord =
              incomeResponse.success && Array.isArray(incomeResponse.data)
                ? incomeResponse.data[0]
                : undefined;
            const incomeNotes =
              incomeNotesResponse.success && Array.isArray(incomeNotesResponse.data)
                ? incomeNotesResponse.data[0]
                : {};
            if (incomeRecord) {
              const mappedBenefits = mapIncomeDataToSchema(
                incomeRecord as any,
                (incomeNotes as any) || {},
                'benefits' as any,
              ) as any;
              if (mappedBenefits?.housingBenefitLocalAllowance) {
                crossSectionSeed.housingBenefitLocalAllowance =
                  mappedBenefits.housingBenefitLocalAllowance;
              }
              // Some warning paths also read the raw mode code.
              if ((incomeRecord as any)?.housingBenefitLocalAllowanceMode) {
                crossSectionSeed.housingBenefitLocalAllowanceMode = (incomeRecord as any)
                  .housingBenefitLocalAllowanceMode;
              }
            }
          } catch (e) {
            // Non-blocking: warning just won't show if we can't hydrate.
            console.warn('HB hydration for rent warning skipped:', e);
          }
        }

        // Include parent hobbiesLeisureSports data for visibleWhen condition evaluation
        let seed = {
          ...crossSectionSeed,
          ...(selected ? { [fieldKey]: selected } : {}),
        };
        // Inject dynamic help text variables so tokens in schema render correctly
        seed = {
          ...seed,
          ...dynamicMeta,
        };
        if (groupKey === 'leisure' && fieldKey === 'gamblingFromHobbiesLeisure') {
          const hobbiesData = (mergedForArrears as any)?.['hobbiesLeisureSports'];
          if (hobbiesData) {
            // Include the parent hobbiesLeisureSports data for visibleWhen evaluation
            seed = {
              hobbiesLeisureSports: hobbiesData,
              [fieldKey]: selected,
            };
          }
        }

        const existingNotesValue =
          selected && typeof selected === 'object' && typeof (selected as any)?.notes === 'string'
            ? ((selected as any).notes as string)
            : undefined;
        lastDomNotesValueRef.current =
          existingNotesValue !== undefined
            ? { signature: fieldSignature, value: existingNotesValue }
            : { signature: fieldSignature };

        // Check if user has edited recently (within last 2 seconds)
        const timeSinceLastEdit = Date.now() - lastUserEditTime.current;
        if (lastUserEditTime.current > 0 && timeSinceLastEdit < 2000) {
          // Only update initial data, not the live refs
          setInitialData(seed);
        } else {
          // Seed both ref and state
          setInitialData(seed);
          liveValuesRef.current = seed;
          latestFormValuesRef.current = seed;
          // Also set global
          globalEditedValues = seed;
          // Reset cleared flags on fresh load
          fieldClearedRef.current = false;
          notesClearedRef.current = false;
        }
      } else {
        setInitialData({});
        liveValuesRef.current = {};
        latestFormValuesRef.current = {};
        globalEditedValues = {};
        fieldClearedRef.current = false;
        notesClearedRef.current = false;
        lastDomNotesValueRef.current = { signature: fieldSignature };
      }
      setFormRenderKey((k) => k + 1);
      setIsDataReady(true);
    } catch (e) {
      console.error('Failed to load field data:', e);
      showAlert({ severity: 'error', message: 'Failed to load field data', autoHide: true, duration: 3000 });
      setIsDataReady(false);
    } finally {
      setLoading(false);
    }
  }, [showAlert, groupKey, fieldKey, fieldSignature]);

  useEffect(() => {
    if (!open) {
      setIsDataReady(false);
      // Clear global values
      globalEditedValues = {};
      globalFormValues = {};
      fieldClearedRef.current = false;
      notesClearedRef.current = false;
      lastDomNotesValueRef.current = { signature: null };
      requestedSignatureRef.current = null;
      return;
    }

    // Wait until the dialog target (groupKey/fieldKey) has synced to the requested signature.
    // This prevents a first-open race where we load data for the previously edited field.
    const requested = requestedSignatureRef.current;
    if (requested && requested !== fieldSignature) {
      return;
    }

    void loadInitial();
  }, [open, fieldSignature, loadInitial]);

  const handleWarningAction = useCallback(
    (action: OpenActionSpec, _metadata?: { varId?: string }): boolean => {
      // Give parent screens first right-of-refusal (profile dialogs, etc.)
      try {
        if (onWarningAction && onWarningAction(action, _metadata)) {
          return true;
        }
      } catch {
        // fall through to local handling
      }

      // Only handle "dialog" actions here. The EditFieldDialog is already a dialog,
      // so we interpret this as "switch the current dialog to the target field".
      if (!action || action.type !== 'dialog') return false;

      const nextGroupKey = mapWarningActionToGroupKey(action.outer, action.inner);
      if (!nextGroupKey) return false;

      const focus = typeof action.focus === 'string' ? action.focus : '';
      const nextFieldKey = focus ? deriveFieldKeyFromFocus(focus) : null;
      if (!nextFieldKey) return false;

      // If the action is targeting the currently-open field, let SpecialScreen handle focusing.
      if (nextGroupKey === groupKey && nextFieldKey === fieldKey) {
        return false;
      }

      // Switch dialog target and re-load data.
      setLoading(true);
      setIsDataReady(false);
      setInitialData({});
      liveValuesRef.current = {};
      latestFormValuesRef.current = {};
      globalEditedValues = {};
      globalFormValues = {};
      fieldClearedRef.current = false;
      notesClearedRef.current = false;
      lastDomNotesValueRef.current = { signature: null };

      requestedSignatureRef.current = `${nextGroupKey}:${nextFieldKey}`;

      setGroupKey(nextGroupKey);
      setFieldKey(nextFieldKey);
      setTitle(`Edit ${nextFieldKey.replace(/([A-Z])/g, ' $1').trim()}`);

      if (focus) {
        pendingFocusPathRef.current = focus;
      }

      // Force SpecialScreen to remount with a fresh form instance for the new field.
      setFormRenderKey((k) => k + 1);
      return true;
    },
    [
      deriveFieldKeyFromFocus,
      fieldKey,
      groupKey,
      mapWarningActionToGroupKey,
      onWarningAction,
    ]
  );

  const handleSubmit = useCallback(
    async (data: unknown, latestValuesFromScreen?: Record<string, unknown>) => {
      try {
        console.log('🚀 handleSubmit called with data:', data);
        console.log('🚀 latestValuesFromScreen:', latestValuesFromScreen);

        setLoading(true);
        isSubmittingRef.current = true; // Mark that we're submitting

        // ===== CRITICAL FIX: SpecialScreen creates duplicate inputs, need to find the ACTIVE one =====
        // Query all inputs with the amount name and get the LAST one (the active one)
        // Try both nested and flat notation
        let allAmountInputs = Array.from(
          document.querySelectorAll(`input[name="${fieldKey}.amount"]`),
        ) as HTMLInputElement[];
        if (allAmountInputs.length === 0) {
          // Try without dot notation
          allAmountInputs = Array.from(
            document.querySelectorAll(`input[name="${fieldKey}"]`),
          ) as HTMLInputElement[];
        }

        // Get the LAST input (the active one that SpecialScreen is using)
        const inputElement = allAmountInputs.length > 0 ? allAmountInputs[allAmountInputs.length - 1] : null;

        // Query frequency element (Select often uses hidden input)
        const allFrequencyInputs = Array.from(
          document.querySelectorAll(`[name="${fieldKey}.frequency"]`),
        ) as (HTMLInputElement | HTMLSelectElement)[];
        const frequencyElement =
          allFrequencyInputs.length > 0 ? allFrequencyInputs[allFrequencyInputs.length - 1] : null;

        // Query notes element
        const allNotesInputs = Array.from(
          document.querySelectorAll(`[name="${fieldKey}.notes"]`),
        ) as (HTMLTextAreaElement | HTMLInputElement)[];
        const notesElement = allNotesInputs.length > 0 ? allNotesInputs[allNotesInputs.length - 1] : null;

        if (inputElement && data && typeof data === 'object') {
          const dataObj = data as Record<string, any>;
          const actualInputValue = inputElement.value;
          const formDataAmount = dataObj[fieldKey]?.amount;

          // CRITICAL: If DOM input differs from form data, the form is stale
          // Trust the DOM input (what user actually entered) over the form data
          if (actualInputValue !== String(formDataAmount)) {
            if (dataObj[fieldKey] && typeof dataObj[fieldKey] === 'object') {
              // Force the form value to match the actual DOM input
              dataObj[fieldKey].amount = actualInputValue;
            }
          }
        }

        const customerId = await resolveCustomerId();
        const userId = getUserId();
        if (!customerId) throw new Error('Customer ID not available');
        if (!userId) throw new Error('User ID not available');

        // Determine if we're dealing with income or asset data
        const isAssetGroup = groupKey === 'properties' || groupKey === 'otherAssets';
        const isIncomeGroup =
          groupKey === 'salary' ||
          groupKey === 'benefits' ||
          groupKey === 'pension' ||
          groupKey === 'otherSources' ||
          groupKey === 'savings';
        const isExpenseGroup = !isAssetGroup && !isIncomeGroup;

        // Fetch base records to merge
        let baseRecord: any = {};
        if (isAssetGroup) {
          const assetResponse = await BudgetService.getAssetData(customerId).catch((err) => {
            console.error('Failed to fetch asset data for asset update:', err);
            return { success: false, data: [] };
          });
          baseRecord =
            assetResponse.success && Array.isArray(assetResponse.data) ? assetResponse.data[0] || {} : {};
          if (!baseRecord || Object.keys(baseRecord).length === 0) {
            baseRecord = { customerId: Number(customerId) };
          }
        } else if (isIncomeGroup) {
          const incomeResponse = await BudgetService.getIncomeData(customerId);
          baseRecord =
            incomeResponse.success && Array.isArray(incomeResponse.data) ? incomeResponse.data[0] || {} : {};
          // Ensure baseRecord has required fields for income update
          if (!baseRecord || !baseRecord.id) {
            console.error('⚠️ No base income record found or missing id:', baseRecord);
            throw new Error('No existing income record found. Please complete the income form first.');
          }
        } else if (isExpenseGroup) {
          const expenseResponse = await BudgetService.getExpenseData(customerId).catch((err) => {
            console.error('Failed to fetch expense data for update:', err);
            return { success: false, data: [] };
          });
          baseRecord =
            expenseResponse.success && Array.isArray(expenseResponse.data) ? expenseResponse.data[0] || {} : {};
        }

        // ===== BUILD SCHEMA DATA FROM THE LATEST FULL FORM SNAPSHOT =====
        // SpecialScreen passes the current form state (mergedLatest) as the second
        // argument. This is the single source of truth for amount, frequency & notes.
        const latestFlatValues = (latestValuesFromScreen || {}) as Record<string, unknown>;
        const normalizedLatest =
          latestFlatValues && Object.keys(latestFlatValues).length > 0 ? normalizeVals(latestFlatValues) : {};

        // Fallback to refs if for some reason we didn't receive latest values
        const refForField =
          (normalizedLatest as Record<string, unknown>)[fieldKey] ??
          (latestFormValuesRef.current as Record<string, unknown>)[fieldKey] ??
          (liveValuesRef.current as Record<string, unknown>)[fieldKey];

        let groupData: Record<string, unknown> =
          refForField && typeof refForField === 'object' && !Array.isArray(refForField)
            ? { ...(refForField as Record<string, unknown>) }
            : {};

        // Overlay the DOM-verified amount if we have it (this is the freshest value).
        if (inputElement) {
          const actualInputValue = inputElement.value;
          groupData = {
            ...groupData,
            amount: actualInputValue,
          };
        }

        // Overlay the DOM-verified notes if we have it
        if (notesElement) {
          const actualNotesValue = notesElement.value;
          groupData = {
            ...groupData,
            notes: actualNotesValue,
          };
        } else if (
          data &&
          typeof data === 'object' &&
          (data as Record<string, any>)[fieldKey] &&
          typeof (data as Record<string, any>)[fieldKey] === 'object'
        ) {
          // As a secondary fallback, overlay any amount from the submit payload
          const submitGroup = (data as Record<string, any>)[fieldKey] as Record<string, unknown>;
          if (submitGroup.amount !== undefined) {
            groupData = {
              ...groupData,
              amount: submitGroup.amount,
            };
          }
        }

        // If user cleared the field, explicitly inject empty amount so mappers
        // treat it as 0 instead of keeping a stale value.
        if (fieldClearedRef.current) {
          groupData = {
            ...groupData,
            amount: '',
          };
        }

        // If user cleared notes, explicitly set empty notes
        if (notesClearedRef.current) {
          groupData = {
            ...groupData,
            notes: '',
          };
        }

        // Always ensure amount exists for mapping (even if empty/undefined)
        if (!('amount' in groupData)) {
          groupData.amount = '';
        }

        // CRITICAL FIX: Always use the most current form values
        // First priority: latestValuesFromScreen (direct from form submission)
        if (latestValuesFromScreen && typeof latestValuesFromScreen === 'object') {
          // Handle both nested and flat structure
          let latestField = (latestValuesFromScreen as any)[fieldKey];

          // Ensure latestField is an object
          if (!latestField || typeof latestField !== 'object') {
            latestField = {};
          } else {
            // Clone it to avoid mutating original
            latestField = { ...latestField };
          }

          // Check flat notation and merge if present (overwriting nested if necessary, or filling gaps)
          const flatFrequency = (latestValuesFromScreen as any)[`${fieldKey}.frequency`];
          const flatNotes = (latestValuesFromScreen as any)[`${fieldKey}.notes`];
          const flatAmount = (latestValuesFromScreen as any)[`${fieldKey}.amount`];

          if (flatAmount !== undefined) latestField.amount = flatAmount;
          if (flatFrequency !== undefined) latestField.frequency = flatFrequency;
          if (flatNotes !== undefined) latestField.notes = flatNotes;

          // Always use the latest values from the form for amount/notes,
          // but for frequency we deliberately trust the DOM hidden input as
          // the single source of truth. React Hook Form can occasionally
          // hold a stale frequency value when multiple Controllers and
          // dot-notation fields are involved (e.g. `takeHomeSalary.frequency`),
          // while the hidden input rendered by `SelectToggle` always reflects
          // the chip that is visually selected in the dialog.
          //
          // So:
          // - amount/notes: take from latestField (falling back to DOM if needed)
          // - frequency: always override from the last DOM element when present.
          if (frequencyElement) {
            groupData.frequency = frequencyElement.value;
          } else if ('frequency' in latestField) {
            groupData.frequency = latestField.frequency;
          }

          if ('notes' in latestField && !notesElement) {
            groupData.notes = latestField.notes ?? ''; // Ensure empty string if null/undefined
          }
          if ('amount' in latestField && !inputElement) {
            groupData.amount = latestField.amount;
          }
        }

        // Second priority: normalizedLatest
        if (normalizedLatest && typeof normalizedLatest === 'object' && (normalizedLatest as any)[fieldKey]) {
          const normalizedField = (normalizedLatest as any)[fieldKey];
          if (typeof normalizedField === 'object') {
            if (!('notes' in groupData) && 'notes' in normalizedField) {
              groupData.notes = normalizedField.notes ?? '';
            }
          }
        }

        // Third priority: ref data
        if (refForField && typeof refForField === 'object') {
          const refObj = refForField as Record<string, unknown>;

          // For notes, only use ref if we don't have it in groupData
          if (!('notes' in groupData) && 'notes' in refObj) {
            groupData.notes = refObj.notes;
          }
        }

        const schemaData: Record<string, unknown> = {
          [fieldKey]: groupData,
        };

        // Debug logging - Only logging the data being sent for update
        console.log('📝 [Update Payload Debug]', {
          field: fieldKey,
          updateData: groupData,
          domCaptured: {
            amount: inputElement ? inputElement.value : 'No',
            frequency: frequencyElement ? frequencyElement.value : 'No',
            notes: notesElement ? notesElement.value : 'No',
          },
        });

        // --- UU Evidence & Document Pre-Save Cleanup ---
        // BEFORE saving, check if this UU benefit field has amount = 0 while
        // a date (evidence period) and/or document still exist. If so, prompt:
        //   "Remove" → delete evidence + document, then proceed to save.
        //   "Keep"   → abort submit (dialog stays open so user can fix the amount).
        try {
          const cleanupRecord = await detectSingleFieldCleanup(
            customerId,
            fieldKey,
            groupData.amount
          );

          if (cleanupRecord) {
            const confirmed = await openConfirmDialog({
              title: 'Remove benefit data',
              message: buildCleanupConfirmMessage([cleanupRecord]),
              confirmText: 'Remove',
              cancelText: 'Keep',
            });

            if (confirmed) {
              const failures = await deleteCleanupRecords(
                [cleanupRecord],
                customerId
              );
              if (failures.length > 0) {
                console.warn('[uuCleanup] Some records could not be deleted:', failures);
                showAlert({
                  severity: 'error',
                  message: 'Some benefit data could not be removed. Please try again.',
                  autoHide: true,
                  duration: 4000,
                });
              }
              // Proceed to save the cleared amount below
            } else {
              // User chose "Keep" — abort submit. Dialog stays open so the
              // user can re-enter the amount. No save happens.
              setLoading(false);
              isSubmittingRef.current = false;
              return;
            }
          }
        } catch (cleanupError) {
          // Non-critical; don't block the save flow
          console.warn('[uuCleanup] Pre-save cleanup check failed:', cleanupError);
        }

        if (isAssetGroup) {
          // Handle asset data update via dedicated asset endpoints
          const mappedAsset = mapSchemaDataToAsset(schemaData as any, groupKey as any, baseRecord as any);
          const mappedAssetNotes = mapSchemaDataToAssetNotes(schemaData as any, groupKey as any);

          const assetUpdatePayload: any = {
            ...(baseRecord as any),
            ...(mappedAsset as any),
            id: baseRecord.id,
            customerId: Number(customerId),
            lastUpdatedBy: Number(userId),
            lastUpdateDate: new Date().toISOString(),
          };

          await BudgetService.updateAssetData(assetUpdatePayload);

          // Update notes via asset notes endpoint - always update to ensure cleared notes are saved
          const assetNotesResponse = await BudgetService.getAssetNotes(customerId);
          const baseAssetNote =
            assetNotesResponse.success && Array.isArray(assetNotesResponse.data)
              ? assetNotesResponse.data[0]
              : undefined;

          // If we have notes to update or we explicitly cleared notes, update them
          if ((Object.keys(mappedAssetNotes).length > 0 || notesClearedRef.current) && baseAssetNote) {
            await BudgetService.updateAssetNotes({
              ...baseAssetNote,
              ...mappedAssetNotes,
              id: baseAssetNote.id,
              customerId: Number(customerId),
              lastUpdatedBy: Number(userId),
              lastUpdateDate: new Date().toISOString(),
            } as any);
          }
        } else if (isIncomeGroup) {
          // Handle income data update
          const mappedIncome = mapSchemaDataToIncome(schemaData as any, groupKey as any, baseRecord as any);
          const mappedIncomeNotes = mapSchemaDataToIncomeNotes(schemaData as any, groupKey as any);

          console.log('💾 Mapped income data:', mappedIncome);
          console.log('📝 Mapped income notes:', mappedIncomeNotes);

          // Ensure baseRecord has required id field
          if (!baseRecord || !baseRecord.id) {
            console.error('⚠️ Base income record missing id field:', baseRecord);
            throw new Error('Can\'t update income: record ID is missing. Please contact support.');
          }

          const incomeUpdatePayload: IncomeUpdateRequest = {
            ...(baseRecord as any),
            ...(mappedIncome as any),
            id: baseRecord.id,
            customerId: Number(customerId),
            lastUpdatedBy: Number(userId),
            lastUpdateDate: new Date().toISOString(),
          };

          await BudgetService.updateIncomeData(incomeUpdatePayload);

          // Update notes - always update to ensure cleared notes are saved
          const incomeNotesResponse = await BudgetService.getIncomeNotes(customerId);
          const baseIncomeNote =
            incomeNotesResponse.success && Array.isArray(incomeNotesResponse.data)
              ? incomeNotesResponse.data[0]
              : undefined;

          // Always update notes if we have a base note record
          if (baseIncomeNote) {
            // Ensure we include the notes field from our current field even if empty
            const fieldMapping = (BUDGET_FIELD_MAPPINGS as any)[groupKey]?.[fieldKey];
            if (fieldMapping?.notes) {
              const notesFieldName = fieldMapping.notes;
              // If notes weren't in mappedIncomeNotes, add them from groupData
              if (!(notesFieldName in mappedIncomeNotes)) {
                (mappedIncomeNotes as any)[notesFieldName] = groupData.notes ?? '';
              }
            }

            console.log('📝 Final notes payload:', mappedIncomeNotes);

            await BudgetService.updateIncomeNotes({
              ...baseIncomeNote,
              ...mappedIncomeNotes,
              id: baseIncomeNote.id,
              customerId: Number(customerId),
              lastUpdatedBy: Number(userId),
              lastUpdateDate: new Date().toISOString(),
            } as any);
          }
        } else {
          // Handle expense data update
          const mappedExpense = mapSchemaDataToExpense(schemaData as any, groupKey as any, baseRecord as any);
          const mappedExpenseNotes = mapSchemaDataToExpenseNotes(schemaData as any, groupKey as any);

          const expenseUpdatePayload: ExpenseUpdateRequest = {
            ...(baseRecord as any),
            ...(mappedExpense as any),
            customerId: Number(customerId),
            lastUpdatedBy: Number(userId),
            lastUpdateDate: new Date().toISOString(),
          } as any;

          await BudgetService.updateExpenseData(expenseUpdatePayload);

          // Update expense notes - always update to ensure cleared notes are saved
          const expenseNotesResponse = await BudgetService.getExpenseNotes(customerId);
          const baseExpenseNote =
            expenseNotesResponse.success && Array.isArray(expenseNotesResponse.data)
              ? expenseNotesResponse.data[0]
              : undefined;

          // Always update notes if we have a base note record
          if (baseExpenseNote) {
            // Ensure we include the notes field from our current field even if empty
            const fieldMapping = (BUDGET_FIELD_MAPPINGS as any)[groupKey]?.[fieldKey];
            if (fieldMapping?.notes) {
              const notesFieldName = fieldMapping.notes;
              // If notes weren't in mappedExpenseNotes, add them from groupData
              if (!(notesFieldName in mappedExpenseNotes)) {
                (mappedExpenseNotes as any)[notesFieldName] = groupData.notes ?? '';
              }
            }

            const notesPayload: ExpensesNoteUpdateRequest = {
              ...(baseExpenseNote as any),
              ...(mappedExpenseNotes as any),
              id: (baseExpenseNote as any).id,
              customerId: Number(customerId),
              lastUpdatedBy: Number(userId),
              lastUpdateDate: new Date().toISOString(),
            };
            await BudgetService.updateExpenseNotes(notesPayload as any);
          }
        }

        // Store what we successfully submitted
        lastSubmittedValues.current = groupData;
        console.log('✅ Successfully saved with values:', groupData);

        // (UU evidence + document cleanup is handled BEFORE the save — see above)

        showAlert({ severity: 'success', message: 'Saved successfully', autoHide: true, duration: 2000 });
        if (onSaved) onSaved();
        onClose();
      } catch (e) {
        console.error('❌ Failed to save field:', e);
        const errorMessage = e instanceof Error ? e.message : 'Failed to save field';
        showAlert({ severity: 'error', message: errorMessage, autoHide: true, duration: 3000 });
      } finally {
        setLoading(false);
        isSubmittingRef.current = false; // Reset submitting flag
        fieldClearedRef.current = false; // Reset cleared flag after submit
        notesClearedRef.current = false; // Reset notes cleared flag after submit
      }
    },
    [getUserId, onClose, onSaved, showAlert, groupKey, fieldKey],
  );

  // put this outside the component or above handlers
  function normalizeVals(vals: Record<string, unknown>) {
    // reuse the same logic as transformDotNotationToNested
    return (function transform(payload: Record<string, unknown>) {
      const result: Record<string, unknown> = {};

      // Pass 1: collect all nested objects (non-dot keys)
      for (const [k, v] of Object.entries(payload)) {
        if (!k.includes('.')) {
          if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
            // Merge nested objects
            result[k] =
              result[k] && typeof result[k] === 'object'
                ? { ...(result[k] as any), ...(v as any) }
                : v;
          } else {
            result[k] = v;
          }
        }
      }

      // Pass 2: handle dot-notation keys only if nested value doesn't exist or is empty
      for (const [k, v] of Object.entries(payload)) {
        if (k.includes('.')) {
          const [p, c] = k.split('.');
          if (!p || !c) continue;

          // Initialize parent if needed
          if (!result[p] || typeof result[p] !== 'object') {
            result[p] = {};
          }

          // Only set if child doesn't already have a non-empty value
          const currentVal = (result[p] as any)[c];
          if (currentVal === undefined || currentVal === null || currentVal === '') {
            (result[p] as any)[c] = v;
          }
        }
      }

      return result;
    })(vals);
  }

  const handleValuesChange = useCallback(
    (vals: Record<string, unknown>) => {
      // Don't update if we're submitting
      if (isSubmittingRef.current) {
        return;
      }

      console.log('🔄 handleValuesChange called with:', vals);

      const normalized = normalizeVals(vals);
      console.log('🔄 Normalized values:', normalized);

      const domNotesInput = document.querySelector(
        `textarea[name="${fieldKey}.notes"]`,
      ) as HTMLTextAreaElement | null;
      if (domNotesInput) {
        const domNotesValue = domNotesInput.value ?? '';
        lastDomNotesValueRef.current = {
          signature: fieldSignature,
          value: domNotesValue,
        };
        const existingGroup = (normalized as Record<string, unknown>)[fieldKey];
        if (existingGroup && typeof existingGroup === 'object' && !Array.isArray(existingGroup)) {
          (existingGroup as Record<string, unknown>).notes = domNotesValue;
        } else {
          (normalized as Record<string, unknown>)[fieldKey] = { notes: domNotesValue };
        }
        (normalized as Record<string, unknown>)[`${fieldKey}.notes`] = domNotesValue;
      } else if (
        lastDomNotesValueRef.current.signature === fieldSignature &&
        lastDomNotesValueRef.current.value !== undefined
      ) {
        const preservedNotes = lastDomNotesValueRef.current.value ?? '';
        const existingGroup = (normalized as Record<string, unknown>)[fieldKey];
        if (existingGroup && typeof existingGroup === 'object' && !Array.isArray(existingGroup)) {
          (existingGroup as Record<string, unknown>).notes = preservedNotes;
        } else {
          (normalized as Record<string, unknown>)[fieldKey] = { notes: preservedNotes };
        }
        (normalized as Record<string, unknown>)[`${fieldKey}.notes`] = preservedNotes;
      }

      // Keep trigger context reactive while typing so limit prompts show instantly
      try {
        const group = (normalized as any)?.[fieldKey];
        const amtRaw =
          group && typeof group === 'object' && !Array.isArray(group)
            ? (group as any).amount
            : (normalized as any)?.[`${fieldKey}.amount`] ?? (vals as any)?.[`${fieldKey}.amount`];
        const freqRaw =
          group && typeof group === 'object' && !Array.isArray(group)
            ? (group as any).frequency
            : (normalized as any)?.[`${fieldKey}.frequency`] ?? (vals as any)?.[`${fieldKey}.frequency`];
        const toNum = (v: unknown): number | undefined => {
          if (typeof v === 'number' && Number.isFinite(v)) return v;
          if (typeof v === 'string') {
            const cleaned = v.replace(/,/g, '').trim();
            if (!cleaned) return undefined;
            const n = Number(cleaned);
            return Number.isFinite(n) ? n : undefined;
          }
          return undefined;
        };
        const next = {
          amount: toNum(amtRaw),
          frequency: typeof freqRaw === 'string' && freqRaw.trim() ? freqRaw : undefined,
        };
        setLiveTriggerValues((prev) => {
          if (prev.amount === next.amount && prev.frequency === next.frequency) return prev;
          return next;
        });
      } catch {
        // ignore
      }

      // Track if user is clearing the amount or notes fields
      const fieldObj = (normalized as any)?.[fieldKey];

      // Also check flat notation values
      const flatNotes = vals[`${fieldKey}.notes`];
      const flatAmount = vals[`${fieldKey}.amount`];

      if (typeof fieldObj === 'object' && !Array.isArray(fieldObj)) {
        const amount = fieldObj?.amount;
        const notes = fieldObj?.notes;

        // Track amount clearing
        if (amount === '' || amount === undefined || amount === null) {
          fieldClearedRef.current = true;
        } else if (amount && String(amount).trim() !== '') {
          fieldClearedRef.current = false;
        }

        // Track notes clearing (check both nested and flat)
        const effectiveNotes = notes !== undefined ? notes : flatNotes;
        if (effectiveNotes === '') {
          notesClearedRef.current = true;
          console.log('📝 Notes cleared to empty string');
        } else if (effectiveNotes !== undefined && effectiveNotes !== null) {
          notesClearedRef.current = false;
        }
      } else if (flatNotes !== undefined || flatAmount !== undefined) {
        // Handle flat notation
        if (flatAmount === '' || flatAmount === undefined || flatAmount === null) {
          fieldClearedRef.current = true;
        } else if (flatAmount && String(flatAmount).trim() !== '') {
          fieldClearedRef.current = false;
        }

        if (flatNotes === '') {
          notesClearedRef.current = true;
          console.log('📝 Notes cleared to empty string (flat notation)');
        } else if (flatNotes !== undefined && flatNotes !== null) {
          notesClearedRef.current = false;
        }
      }

      // Check if values are being reset
      const currentAmount = (latestFormValuesRef.current as any)?.[fieldKey]?.amount;
      const newAmount = (normalized as any)?.[fieldKey]?.amount;
      const currentNotes = (latestFormValuesRef.current as any)?.[fieldKey]?.notes;
      const newNotes = (normalized as any)?.[fieldKey]?.notes;

      // GUARD: Prevent resetting to initial values after user has edited
      // If current ref has a user-edited value (not the initial), and new value is the initial, skip
      const initialAmount = (initialData as any)?.[fieldKey]?.amount;
      const initialNotes = (initialData as any)?.[fieldKey]?.notes;

      // Check if amount is being reset to initial
      if (currentAmount && currentAmount !== initialAmount && newAmount === initialAmount) {
        return; // Don't update!
      }

      // Check if notes are being reset to initial (but allow clearing to empty)
      if (currentNotes !== undefined && currentNotes !== initialNotes && newNotes === initialNotes && newNotes !== '') {
        return; // Don't update!
      }

      // Track that user has edited
      lastUserEditTime.current = Date.now();

      // Update ALL refs
      liveValuesRef.current = normalized;
      latestFormValuesRef.current = normalized;

      // ALSO store in global to survive any resets
      globalEditedValues = normalized;
      globalFormValues = vals; // Store the raw form values too

      // Also try to capture the actual DOM values
      setTimeout(() => {
        const amountInput = document.querySelector(
          `input[name="${fieldKey}.amount"]`,
        ) as HTMLInputElement | null;
        const frequencySelect = document.querySelector(
          `[name="${fieldKey}.frequency"]`,
        ) as HTMLSelectElement | null;
        const notesTextarea = document.querySelector(
          `textarea[name="${fieldKey}.notes"]`,
        ) as HTMLTextAreaElement | null;

        if (amountInput || frequencySelect || notesTextarea) {
          console.log('📍 DOM values captured:', {
            amount: amountInput?.value,
            frequency: frequencySelect?.value,
            notes: notesTextarea?.value,
            fieldKey,
          });
        }
      }, 0);
    },
    [initialData, fieldKey, fieldSignature],
  );

  return (
    <Dialog data-testid="components-budget-edit-field-dialog-modal-action"
      open={open && isDataReady}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { width: '100%', maxWidth: { xs: '100%', md: '742px' } } }}
    >
      <DialogTitle data-testid="components-budget-edit-field-dialog-header-action" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: isMobile ? 2 : 3, px: isMobile ? 2 : 3, pb: isMobile ? 2 : 1 }}>
        <Typography data-testid="components-budget-edit-field-dialog-text-action" variant="h6" component="div" sx={{
          fontWeight: 600,
          fontSize: isMobile ? '20px' : '22px',
          lineHeight: isMobile ? '28px' : '30px',
        }}>
          {title || 'Edit Income'}
        </Typography>
        <IconButton data-testid="components-budget-edit-field-dialog-btn-on-close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider sx={{ ml: isMobile ? 2 : 3, mr: isMobile ? 2 : 3 }} />
      <DialogContent sx={{ pt: isMobile ? 3 : 2, pb: 0, px: isMobile ? 2 : 3 }}>
        {!dialogSchema ? (
          <Box data-testid="components-budget-edit-field-dialog-container-action-1" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 180 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <TriggersProvider value={triggersContext}>
            <SpecialScreen
              key={formRenderKey}
              schema={(niEnhancedSchema ?? enhancedSchema ?? dialogSchema) as unknown as Parameters<typeof SpecialScreen>[0]['schema']}
              hideHeader={true}
              hideActions={true}
              hideFieldCards={true}
              initialData={initialData}
              onWarningAction={handleWarningAction}
              isNorthernIreland={isNorthernIreland}
              onSubmit={(data) => {
                // Always pass the latest form values from the ref as the second parameter
                const latestFormSnapshot = { ...latestFormValuesRef.current };
                // Also include the global form values which might have the flat notation
                const mergedLatest = { ...globalFormValues, ...latestFormSnapshot };
                console.log('🎯 Form submit triggered:', {
                  data,
                  latestFormSnapshot,
                  globalFormValues,
                  mergedLatest,
                });
                handleSubmit(data, mergedLatest);
              }}
              onValuesChange={handleValuesChange}
              formId={formId}
            />
          </TriggersProvider>
        )}
      </DialogContent>
      <Box data-testid="components-budget-edit-field-dialog-container-action-2" sx={{ px: isMobile ? 1 : 2, pt: isMobile ? 1.25 : 2, pb: isMobile ? 1 : 2 }}>
        <Footer
          handleBack={onClose}
          shouldApplyTopBorder={false}
          shouldApplyBackgroundColor={false}
          shouldBackDisabled={true}
          nextButtonText={loading ? 'Saving…' : 'Save Changes'}
          formId={formId}
          nextButtonLoading={Boolean(loading)}
          nextButtonDisabled={Boolean(loading)}
          showSecondaryButton={true}
          secondaryButtonText="Cancel"
          onSecondaryClick={onClose}
          secondaryButtonSx={{
            backgroundColor: '#F2F2F2',
            borderColor: '#F2F2F2',
            color: '#1A1A1A',
            '&:hover': {
              backgroundColor: '#E8E8E8',
              borderColor: '#E8E8E8'
            }
          }}
        />
      </Box>
    </Dialog>
  );
};

export default EditFieldDialog;
